import * as _ from 'lodash';

import { Module } from '../../ts/Module';
import { BaseReactSettingsCtrl, BaseReactSettingsCtrlScope } from '../controllers/BaseReactSettingsCtrl';
import { ISiteSettingsForSiteAdminFormScope } from '../controllers/SiteSettingsForSiteAdminFormCtrl';
import { SectionContainerBaseProps } from '../general-settings/components/GeneralSettingsPageBase';
import { OuterSiteSettingsForSiteAdminProps, SiteSettingsForSiteAdminProps } from '../general-settings/components/ReactSiteSettingsForSiteAdmin';
import { ISiteAdminSettings, ISiteAdminSettingsWithSubscriptions, ISiteSettingsForSiteAdmin } from '../types/ISiteSettingsForSiteAdmin';
import { SettingsFormService } from './tbSettingsCoordinator';

class ReactSiteSettingsForSiteAdminCtrl extends BaseReactSettingsCtrl<OuterSiteSettingsForSiteAdminProps>{
  private static formName: string = 'ReactSiteSettingsForSiteAdminForm';
  private static instanceCount = 0;

  public static $inject = [
    '$scope',
    'SettingsFormService',
  ];

  constructor(
    protected $scope: BaseReactSettingsCtrlScope<any> & SectionContainerBaseProps,
    formService: SettingsFormService
  ) {
    super($scope, formService, ReactSiteSettingsForSiteAdminCtrl.formName + ($scope as any).name);
  }

  $onInit() {
    // This directive uses an isolated scope to bind the value of sections and allow multiple copies of it to be created
    // use the parent scope to access values defined in the parent controller
    const $parent = this.$scope.$parent as ISiteSettingsForSiteAdminFormScope;
    this.$scope.props = {
      ...this.$scope.props,
      settings: {...this.$scope.settings},
      siteId: $parent.siteSettings?.siteId,
      serverSettings: { ...$parent.serverSettings },
      sections: this.$scope.sections,

      // copy overrides
      showEncryptionStatus: $parent.showEncryptionStatus,
      createSite: $parent.createSite,

      // copy changed values to the parent scope and trigger a digest
      mergeIntoParentScope: (values: SiteSettingsForSiteAdminProps) => {
        // SubscriptionSettings needs to be reshaped back to the way the API expects it
        // which is on the same level as siteAdminSettings.
        if(values.settings.subscriptionSettings) {
          $parent.siteSettings.subscriptionSettings = {...values.settings.subscriptionSettings};
        }

        $parent.siteSettings.siteAdminSettings = values.settings;
        this.$scope.$applyAsync();
      }
    };

    // watch changes to the settings to rerender the react component if they change. This is used to provide updated
    // values of the non-migrated settings
    $parent.$on('siteAdminSettings.change', (event: any, newValue: any) => {
      this.$scope.props = {
        ...this.$scope.props,
        settings: newValue
      };
    });

    // Setup to be one way data binding. Site settings currently shouldn't modify the server settings.
    $parent.$on('serverSettings.change', (event: any, newValue: any) => {
      this.$scope.props = {
        ...this.$scope.props,
        serverSettings: newValue
      };
    });
  }
}

/**
 * Instead of transforming the settings object in the settings-coordinator / formik,
 * this is used to transform the settings object before it gets sent to the API.
 * Jon ran into a bunch of problems with deleting the attribute in the angular scope.
 */
export function transformSubscriptionSettingsForApi(siteAdminSettings: ISiteAdminSettings) {
  delete (siteAdminSettings as ISiteAdminSettingsWithSubscriptions).subscriptionSettings;
}

export function transformSubscriptionSettingsForScope(siteSettings: ISiteSettingsForSiteAdmin) {
  (siteSettings.siteAdminSettings as ISiteAdminSettingsWithSubscriptions).subscriptionSettings = _.cloneDeep(siteSettings.subscriptionSettings);
}

/**
 * An adapter to create named settings sections in angular pug files. This simplifies adding new settings using react
 * without worrying about the details of adapting it to angularjs.
 *
 * Usage: just add the directive in siteSettingsForServerAdminForm.pug as follows:
 * tb:-site-settings-for-server-admin-form-adapter(sections="['section1', 'section2', ...]")
 *
 * where section1, section2 are the names of the actual sections to render in the adapter.
 *
 * This adapter can render multiple sections, so if there is an existing adapter in the area you want to add your section
 * you can append your section name to the array without having to add a new adapter.
 */
Module.instance().directive('tbSiteSettingsForSiteAdminFormAdapter', () => {
  return {
    restrict: 'E',
    require: ['^?tbSettingsCoordinator', '^tbSiteSettingsForSiteAdminForm'],
    template: '<tb:react-site-settings-for-site-admin props="props"/>',
    controller: ReactSiteSettingsForSiteAdminCtrl,
    scope: {
      sections: '<',
      settings: '<',
      name: '@',
    }
  };
});
